/**
 * NotFound (design/ux/01_foundations.md §3.12, §6.3; WP-18): a proper page —
 * the title, the address that was requested, « Retour au tableau de bord »
 * (focused), « Rechercher une page (Ctrl K) » (the command palette), and the
 * menu pages close to that address, taken from the navigation tree.
 */
import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Icon } from "@/components/ui/primitives";
import { useKeyboardStore } from "@/stores/keyboardStore.js";
import { useNavStore } from "@/stores/navStore.js";
import { nearbyPages } from "@/lib/nav-tree.js";
import { useNavModel } from "./nav/useNavModel";

export const NotFoundPage: React.FC<{ home: string }> = ({ home }) => {
  const { pathname } = useLocation();
  const model = useNavModel();
  const openPalette = useKeyboardStore((s) => s.openPalette);
  const setTitleOverride = useNavStore((s: any) => s.setTitleOverride);
  const backRef = React.useRef<HTMLAnchorElement>(null);
  const nearby = React.useMemo(() => nearbyPages(model, pathname), [model, pathname]);

  React.useEffect(() => {
    backRef.current?.focus();
  }, [pathname]);
  React.useEffect(() => {
    setTitleOverride?.(pathname, "Page introuvable");
  }, [pathname, setTitleOverride]);

  return (
    <div className="pk-state pk-state--notfound">
      <Icon name="search-x" size={32} className="pk-state-icon" />
      <h1 id="nf-title" className="pk-state-title">
        Page introuvable
      </h1>
      <p className="pk-state-text">
        L'adresse <span className="font-mono">{pathname}</span> ne correspond à aucune page.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button as={Link} ref={backRef as never} to={home} icon="layout-dashboard">
          Retour au tableau de bord
        </Button>
        <Button variant="secondary" icon="search" shortcut="Ctrl+K" onClick={openPalette}>
          Rechercher une page
        </Button>
      </div>
      {nearby.length > 0 && (
        <nav aria-labelledby="nf-nearby" className="pk-state-nearby">
          <h2 id="nf-nearby" className="pk-state-nearby-title">
            Pages de cette rubrique
          </h2>
          <ul>
            {nearby.map((p) => (
              <li key={p.id}>
                <Link to={p.to}>{p.label}</Link>
                <span className="pk-state-nearby-path">{p.path}</span>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

export default NotFoundPage;
